import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AgregarItemGestionTiempoReq,
  EliminarItemsGestionTiempoReq,
  GestionTiempoRes,
} from '@core/interfaces/request-response/gestion-tiempo.dto.interface';
import {
  calcularTiempoRestanteGestion,
  carritoProductoToGestionTiempoItem,
  formatearTiempo,
  gestionTiempoItemToCarritoProducto,
} from '@shared/utils/gestionTiempo.utils';
import { UiDialog } from '@shared/components/ui';
import { AgregarProducto2 } from '../agregar-producto-2/agregar-producto-2';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { GestionTiempoApiService } from '@core/services/api/gestion-tiempo-api.service';
import { ToastService } from '@shared/services/toast.service';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceService } from '@shared/services/device.service';
import { ProcesarVenta } from '../procesar-venta/procesar-venta';
import { CarritoCompraService } from '@shared/services/carrito-compra.service';
import { ModalConfirmacion } from '../modal-confirmacion/modal-confirmacion';

@Component({
  selector: 'app-card-gestion-tiempo',
  imports: [DatePipe, CurrencyPipe, UiDialog, AgregarProducto2, ProcesarVenta, ModalConfirmacion],
  templateUrl: './card-gestion-tiempo.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './card-gestion-tiempo.css',
})
export class CardGestionTiempo {
  // Servicios
  private readonly deviceService = inject(DeviceService);
  private readonly gestionTiempoApiService = inject(GestionTiempoApiService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly carritoCompraService = inject(CarritoCompraService);
  // datos
  private estadoAnterior = signal<string | null>(null);
  private readonly now = signal<number>(Date.now());
  modal_agregar_producto = signal<boolean>(false);
  modal_procesar_venta = signal<boolean>(false);
  gestion_tiempo = input.required<GestionTiempoRes>();
  index = input<number>(0);
  emitirVenta = output<void>();
  modal_confirmacion_eliminar = signal<boolean>(false);

  // Calcula el delay de animación único para cada card
  animationDelay = computed(() => `${this.index() * 0.5}s`);

  constructor() {
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(Date.now()));

    // Effect para reproducir sonido cuando cambie a terminado
    effect(() => {
      if (this.deviceService.isBrowser() == true) {
        const estadoActual = this.estado();
        const estadoPrevio = this.estadoAnterior();
        if (estadoActual === 'terminado' && estadoPrevio !== 'terminado' && estadoPrevio !== null) {
          this.reproducirSonido();
        }
        this.estadoAnterior.set(estadoActual);
      }
    }, { allowSignalWrites: true });
  }

  private tiempo_restante_seg = computed(() => {
    this.now();
    return calcularTiempoRestanteGestion(this.gestion_tiempo());
  });
  tiempo_formateado = computed(() => {
    const seg = this.tiempo_restante_seg();
    if (seg === 0 || seg === null) {
      return 'TERMINADO';
    }
    return formatearTiempo(seg);
  });
  estado = computed(() => {
    const seg_restantes = this.tiempo_restante_seg();
    if (seg_restantes === null) {
      return 'sin-tiempo';
    } else if (seg_restantes <= 0) {
      return 'terminado';
    } else if (seg_restantes <= 300) {
      return 'proximo-terminar';
    } else {
      return 'activo';
    }
  });
  titulo_estado = computed(() => {
    switch (this.estado()) {
      case 'activo':
        return '🧡 En curso';
      case 'proximo-terminar':
        return '⚠️ Por terminar';
      case 'terminado':
        return '🚫 Terminado';
      default:
        return 'ℹ️ Sin tiempo asignado';
    }
  });
  calcular_total = computed(() => {
    return this.gestion_tiempo().items.reduce((acc, item) => acc + (Number(item.subtotal) || 0), 0);
  });
  calcular_hora_termino = computed(() => {
    const { inicio, items } = this.gestion_tiempo();

    const totalMinutos = items.reduce(
      (acc, item) => acc + (Number(item.duracion_minutos) || 0) * item.cantidad,
      0,
    );

    const inicioDate = inicio instanceof Date ? inicio : new Date(inicio);
    const fin = new Date(inicioDate.getTime() + totalMinutos * 60000);
    return fin;
  });

  // Meteodos
  productos_carrito = computed<IProductoCarritoData[]>(() => {
    return gestionTiempoItemToCarritoProducto(this.gestion_tiempo().items);
  });
  onEmitirVenta() {
    this.emitirVenta.emit();
  }

  onAgregarProductos(data: IProductoCarritoData[]) {
    const rq: AgregarItemGestionTiempoReq = {
      gestion_tiempo_id: this.gestion_tiempo().id,
      items: carritoProductoToGestionTiempoItem(data).map(({ id, ...item }) => item),
    };
    this.gestionTiempoApiService.agregarItems(rq).subscribe({
      next: (rs) => {
        this.modal_agregar_producto.set(false);
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'Los productos se han agregado correctamente a la gestión de tiempo.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err?.error?.message ||
            'No se pudieron agregar los productos a la gestión de tiempo. Por favor, intenta nuevamente.',
        );
      },
    });
  }
  onEliminarProducto(index: number) {
    const item = this.gestion_tiempo().items.at(index);
    if (!item || item.id == null) return;

    const rq: EliminarItemsGestionTiempoReq = {
      gestion_tiempo_id: this.gestion_tiempo().id,
      item_ids: index === -1 ? [-1] : [item.id],
    };

    this.gestionTiempoApiService.eliminarItems(rq).subscribe({
      next: (rs) => {
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          rs.message || 'El producto se ha eliminado correctamente de la gestión de tiempo.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err?.error?.message ||
            'No se pudo eliminar el producto de la gestión de tiempo. Por favor, intenta nuevamente.',
        );
      },
    });
  }
  finalizarSesion() {
    this.carritoCompraService.limpiar();
    this.carritoCompraService.cargarProductosGestionTiempo(this.gestion_tiempo().items);
    this.modal_procesar_venta.set(true);
  }

  eliminarGestionTiempo() {
    this.gestionTiempoApiService.eliminar(this.gestion_tiempo().id).subscribe({
      next: (rs) => {
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          rs.message || 'La gestión de tiempo se ha eliminado correctamente.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err?.error?.message ||
            'No se pudo eliminar la gestión de tiempo. Por favor, intenta nuevamente.',
        );
      },
    });
  }

  private reproducirSonido(): void {
    try {
      const audio = new Audio('/soung/expired.mp3');
      audio.onerror = () => {
        const fallbackAudio = new Audio('/soung/expired.ogg');
        fallbackAudio.play().catch((error) => {
          console.error('Error al reproducir el sonido de fallback:', error);
        });
      };
      audio.play().catch((error) => {
        console.error('Error al reproducir el sonido:', error);
      });
    } catch (error) {
      console.error('Error al crear el audio:', error);
    }
  }
}
